import React, { useState, useMemo } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@material-ui/core';
import { Field } from '@campfire/field/lib';
import { TabletButton } from '@campfire/tablet-button/lib';
import { Form, Formik } from 'formik';
import { object as YupObject, string as YupString, boolean as YupBoolean, number as YupNumber } from 'yup';
import { SwitchField } from './field-task-item-dialog/additional-field-type-fields/SwitchField';
import { TaskStage } from '../../admin-console-volunteer-profile-model';
import { AlertCard, getAlertCardColors } from '../../../../../../../common/cards/alert-card/AlertCard';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (taskStage: Omit<TaskStage, 'taskStageId'>) => Promise<void>;
  onDelete?: () => Promise<void>;
  editingStage?: TaskStage | null;
  existingStages?: TaskStage[];
  isLoading?: boolean;
  hasTasksInStage?: boolean;
}

interface FormValues {
  name: string;
  automateApproval: boolean;
  order: number;
}

const validationSchema = YupObject().shape({
  name: YupString().required('Stage name is required'),
  automateApproval: YupBoolean().required(),
  order: YupNumber()
    .required('Order is required')
    .min(1, 'Order must be at least 1'),
});

export const AddTaskStageDialog = ({
  open,
  onClose,
  onSubmit,
  onDelete,
  editingStage,
  existingStages = [],
  isLoading = false,
  hasTasksInStage = false,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { lightTextColor } = getAlertCardColors('warning');

  // Calculate suggested order based on context
  const getSuggestedOrder = useMemo(() => {
    if (existingStages.length === 0) return 1;
    const existingOrders = existingStages.map((stage) => stage.order).sort((a, b) => a - b);

    // If editing and user is trying to change order, suggest keeping current order or next available
    if (editingStage) {
      return editingStage.order; // Suggest keeping the current order
    }

    // For new stages, suggest next available
    return Math.max(...existingOrders) + 1;
  }, [existingStages, editingStage]);

  const initialValues: FormValues = {
    name: editingStage?.name || '',
    automateApproval: editingStage?.automateApproval || false,
    order: editingStage?.order || getSuggestedOrder,
  };

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: values.name,
        automateApproval: values.automateApproval,
        order: values.order,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>{editingStage ? 'Edit Task Stage' : 'Add Task Stage'}</DialogTitle>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, isValid }) => {
            // Check if the current order conflicts with existing stages
            const existingStageWithOrder = existingStages.find(
              (stage) => stage.order === values.order && stage.taskStageId !== editingStage?.taskStageId
            );

            const hasOrderConflict = !!existingStageWithOrder;

            return (
              <Form>
                <DialogContent style={{ paddingTop: hasOrderConflict ? undefined : '8px' }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant='body2' color='textSecondary' gutterBottom>
                        Create a new stage that tasks can be organised into. Stages allow you to group tasks together
                        and stagger approval or volunteer completion of tasks.
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Field
                        name='name'
                        label='Stage Name'
                        placeholder='e.g. Onboarding, Training, Compliance'
                        required
                        fullWidth
                        variant='outlined'
                        autoFocus
                        disabled={isSubmitting || isLoading}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Field
                        name='order'
                        label='Order'
                        type='number'
                        required
                        fullWidth
                        variant='outlined'
                        inputProps={{ min: 1 }}
                        disabled={isSubmitting || isLoading}
                      />
                    </Grid>
                    {hasOrderConflict && (
                      <Grid item xs={12}>
                        <AlertCard variant='warning'>
                          <Typography
                            variant='subtitle2'
                            display='inline'
                            style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}
                          >
                            A stage with position {values.order} already exists: &ldquo;
                            <strong>{existingStageWithOrder?.name}</strong>&rdquo;.
                            {editingStage ? (
                              <> You can keep the current position {editingStage.order} or choose a different number.</>
                            ) : (
                              <> Please choose a different position.</>
                            )}
                          </Typography>
                          {editingStage && (
                            <div style={{ marginTop: '8px' }}>
                              <TabletButton
                                size='small'
                                variant='outlined'
                                onClick={() => setFieldValue('order', editingStage.order)}
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                              >
                                Keep Position {editingStage.order}
                              </TabletButton>
                            </div>
                          )}
                        </AlertCard>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <SwitchField
                        title='Automate Approval'
                        description='All tasks in this stage will automatically approve submissions without manual review'
                        checked={values.automateApproval}
                        onChange={() => setFieldValue('automateApproval', !values.automateApproval)}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions
                  style={{
                    justifyContent: editingStage && onDelete && !hasTasksInStage ? 'space-between' : 'flex-end',
                  }}
                >
                  {editingStage && onDelete && !hasTasksInStage && (
                    <TabletButton
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isSubmitting || isDeleting || isLoading}
                      style={{ color: '#f44336' }}
                    >
                      Delete Stage
                    </TabletButton>
                  )}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <TabletButton onClick={onClose} disabled={isSubmitting || isDeleting || isLoading}>
                      Cancel
                    </TabletButton>
                    <TabletButton
                      type='submit'
                      color='primary'
                      disabled={!isValid || hasOrderConflict || isSubmitting || isDeleting || isLoading}
                    >
                      {isSubmitting || isLoading ? 'Saving...' : editingStage ? 'Save Changes' : 'Add Stage'}
                    </TabletButton>
                  </div>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Delete Task Stage</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the stage &ldquo;{editingStage?.name}&rdquo;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <TabletButton onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
            Cancel
          </TabletButton>
          <TabletButton onClick={handleDelete} style={{ color: '#f44336' }} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </TabletButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
